import { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { employeesApi, rolesApi, storesApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmployeePasswordPanel } from '@/components/employees/EmployeePasswordPanel';
import { EmploymentStatusBadge } from '@/components/employees/EmploymentStatusBadge';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useActiveCompanies } from '@/hooks/useActiveCompanies';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import type { EmployeeRequest, EmployeeResponse, EmployeeStatusHistoryResponse } from '@/types/employee';
import type { RoleResponse } from '@/types/role';
import type { StoreResponse } from '@/types/store';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { PlanLimitNotice } from '@/components/ui/PlanLimitNotice';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { isOrgOwner } from '@/utils/moduleAccess';

const empty: EmployeeRequest = { employeeCode: '', firstName: '' };

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'TERMINATED', label: 'Terminated' },
  { value: 'RESIGNED', label: 'Resigned' },
  { value: 'RETIRED', label: 'Retired' },
];

const INACTIVE_REASONS = [
  { value: 'LONG_ABSENCE', label: 'Long absence' },
  { value: 'ABSCONDED', label: 'Absconded' },
  { value: 'EXTENDED_LEAVE', label: 'Extended leave' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'CONTRACT_PAUSED', label: 'Contract paused' },
  { value: 'CUSTOM', label: 'Custom reason' },
];

export function EmployeesPage() {
  const selectedCompany = useAppSelector((s) => s.app.selectedCompany);
  const { companies } = useActiveCompanies();
  const { atEmployeeLimit, employeeLimitMessage, reload: reloadLimits } = usePlanLimits();
  const { has, isMaster, permissions, roleCode } = usePermissions();
  const isSuperAdmin = isMaster || isOrgOwner(permissions, roleCode);
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<EmployeeResponse[]>([]);
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [stores, setStores] = useState<StoreResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeResponse | null>(null);
  const [form, setForm] = useState<EmployeeRequest>(empty);
  const [saving, setSaving] = useState(false);
  const [statusHistory, setStatusHistory] = useState<EmployeeStatusHistoryResponse[]>([]);
  const [statusForm, setStatusForm] = useState({
    employmentStatus: 'ACTIVE',
    reason: '',
    comments: '',
  });
  const [canManagePasswords, setCanManagePasswords] = useState(false);

  useEffect(() => {
    rolesApi.listAssignable().then(setRoles).catch(() => {});
    storesApi.list({ size: 100, activeOnly: true }).then((p) => setStores(p.content)).catch(() => {});
    if (has('EMPLOYEE_MANAGE')) {
      employeesApi.passwordManagementAccess().then(setCanManagePasswords).catch(() => {});
    }
  }, [has]);

  const roleOptions = useMemo(
    () =>
      roles.map((r) => ({
        value: String(r.id),
        label: r.systemRole ? `${r.name} (built-in)` : r.name,
      })),
    [roles],
  );

  const companyOptions = useMemo(
    () =>
      companies.map((c) => ({
        value: String(c.id),
        label: `${c.name} (${c.code})`,
      })),
    [companies],
  );

  const formCompanyId = form.companyId ?? selectedCompany?.id;

  const assignableStores = useMemo(() => {
    if (!formCompanyId) return stores;
    return stores.filter((s) => s.companyId == null || s.companyId === formCompanyId);
  }, [stores, formCompanyId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeesApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const loadStatusHistory = async (employeeId: number) => {
    try {
      const history = await employeesApi.statusHistory(employeeId);
      setStatusHistory(history);
    } catch {
      setStatusHistory([]);
    }
  };

  const toggleStore = (storeId: number) => {
    const ids = form.storeIds ?? [];
    setForm({
      ...form,
      storeIds: ids.includes(storeId) ? ids.filter((id) => id !== storeId) : [...ids, storeId],
    });
  };

  const openEdit = (r: EmployeeResponse) => {
    setEditing(r);
    setForm({
      employeeCode: r.employeeCode,
      firstName: r.firstName,
      lastName: r.lastName,
      email: r.email,
      phone: r.phone,
      roleId: r.roleId,
      designation: r.designation,
      companyId: r.companyId,
      storeIds: r.storeIds ?? [],
    });
    setStatusForm({
      employmentStatus: r.employmentStatus ?? 'ACTIVE',
      reason: r.statusChangeReason ?? '',
      comments: r.statusChangeComments ?? '',
    });
    setModalOpen(true);
    loadStatusHistory(r.id);
  };

  const columns: Column<EmployeeResponse>[] = [
    { key: 'employeeCode', header: 'Code' },
    {
      key: 'firstName',
      header: 'Name',
      render: (r) => `${r.firstName} ${r.lastName ?? ''}`.trim(),
    },
    { key: 'email', header: 'Email' },
    {
      key: 'company',
      header: 'Company',
      render: (r) => r.companyName ?? '—',
    },
    {
      key: 'role',
      header: 'App role',
      render: (r) => r.roleName ?? '—',
    },
    {
      key: 'login',
      header: 'Login',
      render: (r) => (r.hasLogin ? 'Yes' : 'No'),
    },
    { key: 'designation', header: 'Designation' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <EmploymentStatusBadge status={r.employmentStatus} />,
    },
    {
      key: 'reason',
      header: 'Inactive reason',
      render: (r) =>
        r.employmentStatus && r.employmentStatus !== 'ACTIVE'
          ? (r.statusChangeReason?.replace(/_/g, ' ') ?? '—')
          : '—',
    },
    {
      key: 'edit',
      header: '',
      render: (r) => (
        <button type="button" className="text-primary-600" onClick={() => openEdit(r)}>
          Edit
        </button>
      ),
    },
  ];

  const save = async () => {
    if (!form.companyId && !selectedCompany?.id) {
      notify.error('Select a company for this employee');
      return;
    }
    if (form.email?.trim() && !form.roleId) {
      notify.error('Select an app access role when email is provided');
      return;
    }
    const payload: EmployeeRequest = {
      ...form,
      companyId: form.companyId ?? selectedCompany?.id,
    };
    setSaving(true);
    try {
      if (editing) {
        await employeesApi.update(editing.id, payload);
        const statusChanged =
          (editing.employmentStatus ?? 'ACTIVE') !== statusForm.employmentStatus ||
          (statusForm.employmentStatus !== 'ACTIVE' && statusForm.reason);
        if (statusChanged) {
          if (statusForm.employmentStatus !== 'ACTIVE' && !statusForm.reason) {
            notify.error('Reason is required for non-active employment status');
            setSaving(false);
            return;
          }
          await employeesApi.changeStatus(editing.id, {
            employmentStatus: statusForm.employmentStatus,
            reason: statusForm.reason || undefined,
            comments: statusForm.comments || undefined,
          });
        }
      } else {
        await employeesApi.create(payload);
      }
      notify.success(editing ? 'Employee updated' : 'Employee created');
      setModalOpen(false);
      load();
      reloadLimits();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const showPasswordPanel = isSuperAdmin && canManagePasswords && editing;

  return (
    <div>
      <PageHeader
        title="Employees"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Employees' }]}
        actions={
          <Button
            disabled={atEmployeeLimit}
            title={atEmployeeLimit ? employeeLimitMessage ?? '' : undefined}
            onClick={() => {
              setEditing(null);
              setForm({ ...empty, companyId: selectedCompany?.id });
              setStatusForm({ employmentStatus: 'ACTIVE', reason: '', comments: '' });
              setStatusHistory([]);
              setModalOpen(true);
            }}
          >
            <AddIcon fontSize="small" /> Add employee
          </Button>
        }
      />
      {atEmployeeLimit && employeeLimitMessage && <PlanLimitNotice message={employeeLimitMessage} />}

      <DataTable
        columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit employee' : 'New employee'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {companyOptions.length > 0 && (
            <Select
              label="Company *"
              placeholder="Select company"
              options={companyOptions}
              value={form.companyId != null ? String(form.companyId) : ''}
              onChange={(v) => {
                const nextCompanyId = v ? Number(v) : undefined;
                const allowedStoreIds = new Set(
                  stores
                    .filter((s) => !nextCompanyId || s.companyId == null || s.companyId === nextCompanyId)
                    .map((s) => s.id),
                );
                setForm({
                  ...form,
                  companyId: nextCompanyId,
                  storeIds: (form.storeIds ?? []).filter((id) => allowedStoreIds.has(id)),
                });
              }}
              className="sm:col-span-2"
            />
          )}
          <Input
            label="Employee code *"
            value={form.employeeCode}
            onChange={(e) => setForm({ ...form, employeeCode: e.target.value })}
          />
          <Input
            label="First name *"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <Input
            label="Last name"
            value={form.lastName ?? ''}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
          <Input
            label="Designation"
            value={form.designation ?? ''}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
          />
          <Input
            label="Email (for app login)"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="Phone"
            value={form.phone ?? ''}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Select
            label="App access role"
            placeholder="Select role (required if email set)"
            options={roleOptions}
            value={form.roleId != null ? String(form.roleId) : ''}
            onChange={(v) =>
              setForm({
                ...form,
                roleId: v ? Number(v) : undefined,
              })
            }
          />
        </div>

        {editing && (
          <div className="mt-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
            <p className="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-200">Employment status</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={statusForm.employmentStatus}
                onChange={(v) =>
                  setStatusForm((s) => ({
                    ...s,
                    employmentStatus: v,
                    reason: v === 'ACTIVE' ? '' : s.reason,
                  }))
                }
              />
              {statusForm.employmentStatus !== 'ACTIVE' && (
                <>
                  <Select
                    label="Reason *"
                    placeholder="Select reason"
                    options={INACTIVE_REASONS}
                    value={statusForm.reason}
                    onChange={(v) => setStatusForm((s) => ({ ...s, reason: v }))}
                  />
                  <Input
                    label="Comments"
                    value={statusForm.comments}
                    onChange={(e) => setStatusForm((s) => ({ ...s, comments: e.target.value }))}
                    className="sm:col-span-2"
                  />
                </>
              )}
            </div>
            {statusHistory.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Status timeline</p>
                <ul className="space-y-2 text-sm">
                  {statusHistory.map((h) => (
                    <li key={h.id} className="rounded bg-slate-50 px-3 py-2 dark:bg-slate-800">
                      <span className="font-medium">{h.oldStatus ?? '—'} → {h.newStatus}</span>
                      {h.reason && <span className="ml-2 text-slate-500">({h.reason.replace(/_/g, ' ')})</span>}
                      <span className="block text-xs text-slate-500">
                        {h.changedByName ?? 'System'} · {h.createdAt ? new Date(h.createdAt).toLocaleString() : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {assignableStores.length > 0 && (
          <div className="mt-4">
            <p className="label">Assigned stores</p>
            <div className="flex flex-wrap gap-2">
              {assignableStores.map((s) => (
                <label
                  key={s.id}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm dark:border-slate-600"
                >
                  <input
                    type="checkbox"
                    checked={(form.storeIds ?? []).includes(s.id)}
                    onChange={() => toggleStore(s.id)}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {showPasswordPanel && (
          <EmployeePasswordPanel
            employeeId={editing.id}
            hasLogin={editing.hasLogin ?? false}
            email={editing.email}
          />
        )}
      </Modal>
    </div>
  );
}
